import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import {
  CONSENT_EVENT,
  CONSENT_STORAGE_KEY,
  type CookieConsent,
} from '@/lib/cookieConsent'

const PIXEL_SCRIPT_ID = 'meta-pixel-script'

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void
      queue: unknown[]
      push: (...args: unknown[]) => void
      loaded: boolean
      version: string
    }
    _fbq?: unknown
  }
}

function readConsent(): CookieConsent | null {
  try {
    const v = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (v === 'accepted' || v === 'essential') return v
  } catch {
    /* ignore */
  }
  return null
}

function pixelId(): string {
  return String(import.meta.env.VITE_META_PIXEL_ID || '').trim()
}

/** Prod marketing hosts only — shared UAT/prod builds must not mix Pixel traffic. */
function isPixelHost(): boolean {
  const raw = String(
    import.meta.env.VITE_META_PIXEL_HOSTS || 'www.shopquay.com,shopquay.com',
  )
  const allowed = new Set(
    raw
      .split(',')
      .map((h) => h.trim().toLowerCase())
      .filter(Boolean),
  )
  return allowed.has(window.location.hostname.toLowerCase())
}

/** Skip tenant payment/form flows that sit under PublicLayout. */
function isMarketingPath(pathname: string): boolean {
  if (pathname.startsWith('/forms')) return false
  if (pathname.startsWith('/pay')) return false
  return true
}

function ensureFbqStub() {
  if (window.fbq) return

  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args)
    } else {
      fbq.queue.push(args)
    }
  } as NonNullable<Window['fbq']>

  fbq.push = fbq
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.queue = []
  window.fbq = fbq
  if (!window._fbq) window._fbq = fbq
}

function loadPixelScript() {
  if (document.getElementById(PIXEL_SCRIPT_ID)) return
  const t = document.createElement('script')
  t.id = PIXEL_SCRIPT_ID
  t.async = true
  t.src = 'https://connect.facebook.net/en_US/fbevents.js'
  const s = document.getElementsByTagName('script')[0]
  s?.parentNode?.insertBefore(t, s)
}

/**
 * ShopQuay marketing Meta Pixel (www.shopquay.com / shopquay.com only).
 * Same codebase may ship to DEV/UAT with this ID baked in; hostname gate prevents mix-up.
 * Also requires cookie consent "accepted". Not used on tenant storefronts.
 */
export default function MetaPixel() {
  const { pathname } = useLocation()
  const initedForId = useRef<string | null>(null)

  useEffect(() => {
    const id = pixelId()
    if (!id || !isPixelHost()) return

    const sync = () => {
      if (readConsent() !== 'accepted' || !isMarketingPath(pathname)) return

      if (initedForId.current !== id) {
        ensureFbqStub()
        loadPixelScript()
        window.fbq?.('init', id)
        initedForId.current = id
      }
      window.fbq?.('track', 'PageView')
    }

    sync()

    const onConsent = (e: Event) => {
      if ((e as CustomEvent<CookieConsent>).detail === 'accepted') sync()
    }
    window.addEventListener(CONSENT_EVENT, onConsent)
    return () => window.removeEventListener(CONSENT_EVENT, onConsent)
  }, [pathname])

  return null
}
